import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

const FONCTIONS = ['Dirigeant','DRH','Responsable formation','Responsable achat','Commercial','Comptable','Autre'];

function ModalContact({ contact, clients, onSave, onClose }) {
  const isEdit = !!contact?.id;
  const [form, setForm] = useState({
    client_id:    contact?.client_id    || '',
    nom:          contact?.nom          || '',
    prenom:       contact?.prenom       || '',
    fonction:     contact?.fonction     || '',
    email:        contact?.email        || '',
    telephone:    contact?.telephone    || '',
    mobile:       contact?.mobile       || '',
    est_decideur: contact?.est_decideur || false,
    optin_email:  contact?.optin_email  || false,
    notes:        contact?.notes        || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  async function handleSave() {
    if (!form.client_id || !form.nom) return setError('Client et nom requis');
    setSaving(true); setError('');
    try {
      const body = { ...form };
      if (isEdit) {
        await apiFetch(`/crm/contacts/${contact.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/crm/contacts', { method: 'POST', body: JSON.stringify(body) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Modifier le contact' : 'Nouveau contact'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-grid-2">
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Client *</label>
              <select style={inputStyle} value={form.client_id}
                onChange={e => set('client_id', e.target.value)}>
                <option value="">Sélectionner…</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Nom *</label>
              <input style={inputStyle} value={form.nom}
                onChange={e => set('nom', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Prénom</label>
              <input style={inputStyle} value={form.prenom}
                onChange={e => set('prenom', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Fonction</label>
              <select style={inputStyle} value={form.fonction}
                onChange={e => set('fonction', e.target.value)}>
                <option value="">—</option>
                {FONCTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Email</label>
              <input type="email" style={inputStyle} value={form.email}
                onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Téléphone</label>
              <input style={inputStyle} value={form.telephone}
                onChange={e => set('telephone', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Mobile</label>
              <input style={inputStyle} value={form.mobile}
                onChange={e => set('mobile', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes</label>
              <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }}
                value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.est_decideur}
                onChange={e => set('est_decideur', e.target.checked)} />
              Décideur
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.optin_email}
                onChange={e => set('optin_email', e.target.checked)} />
              Opt-in email
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMContacts() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [clients, setClients]   = useState([]);
  const [search, setSearch]     = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);

  const LIMIT = 30;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (search)       params.set('search', search);
    if (filterClient) params.set('client_id', filterClient);
    apiFetch(`/crm/contacts?${params}`)
      .then(r => { setItems(r.data || []); setTotal(r.total || 0); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, search, filterClient]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/crm/clients?limit=500').then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  async function handleDelete(id) {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await apiFetch(`/crm/contacts/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  return (
    <div className="crm-section-page">
      <div className="crm-toolbar">
        <div className="crm-filters">
          <input className="crm-search" placeholder="Rechercher…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="crm-select" value={filterClient}
            onChange={e => { setFilterClient(e.target.value); setPage(1); }}>
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
          </select>
        </div>
        <button className="btn btn-primary crm-btn-sm"
          onClick={() => { setEditing(null); setShowModal(true); }}>
          + Contact
        </button>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Fonction</th>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Décideur</th>
                  <th>Opt-in</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={8} className="crm-empty-row">Aucun contact trouvé</td></tr>
                )}
                {items.map(ct => (
                  <tr key={ct.id} className="crm-table-row">
                    <td>
                      <strong>{ct.prenom ? `${ct.prenom} ${ct.nom}` : ct.nom}</strong>
                    </td>
                    <td className="crm-text-sm">{ct.fonction || '—'}</td>
                    <td className="crm-text-sm">{ct.nom_client || '—'}</td>
                    <td>
                      {ct.email
                        ? <a href={`mailto:${ct.email}`} className="crm-link">{ct.email}</a>
                        : '—'}
                    </td>
                    <td className="crm-text-sm">{ct.telephone || ct.mobile || '—'}</td>
                    <td className="crm-text-center">
                      {ct.est_decideur ? <span className="crm-badge" style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399' }}>✓</span> : ''}
                    </td>
                    <td className="crm-text-center">
                      {ct.optin_email ? <span className="crm-badge" style={{ background: 'rgba(74,158,255,0.12)', color: '#4a9eff' }}>✓</span> : ''}
                    </td>
                    <td>
                      <button className="crm-btn-edit"
                        onClick={() => { setEditing(ct); setShowModal(true); }}>✎</button>
                      <button className="crm-btn-edit crm-card-btn--danger"
                        onClick={() => handleDelete(ct.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > LIMIT && (
            <div className="crm-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span>Page {page} / {Math.ceil(total / LIMIT)}</span>
              <button disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
          <div className="crm-count">{total} contact{total > 1 ? 's' : ''}</div>
        </>
      )}

      {showModal && (
        <ModalContact
          contact={editing}
          clients={clients}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
